import { createContext, useContext, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { useAuth } from '@/hooks/useAuth';
import type { Profile } from '@/types/auth';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAuthenticated: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  updateProfile: (updates: Partial<Pick<Profile, 'displayName' | 'avatarUrl' | 'isFindable' | 'defaultTeamId' | 'motivationReminderEnabled' | 'motivationReminderTime' | 'timezone' | 'lastSeenAt'>>) => Promise<{ error: Error | null }>;
  joinTeam: (teamId: string, asAdmin?: boolean) => Promise<{ error: Error | null }>;
  leaveTeam: (teamId: string) => Promise<{ error: Error | null }>;
  promoteToAdmin: (teamId: string, targetUserId: string) => Promise<{ error: Error | null }>;
  demoteFromAdmin: (teamId: string, targetUserId: string) => Promise<{ error: Error | null }>;
  removeMemberFromTeam: (teamId: string, targetUserId: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function createMissingAuthContextFallback(): AuthContextType {
  const notReady = async () => ({ error: new Error('Auth context unavailable') });
  return {
    user: null,
    session: null,
    profile: null,
    loading: false,
    isAuthenticated: false,
    signInWithEmail: notReady,
    signUpWithEmail: notReady,
    signInWithGoogle: notReady,
    signOut: notReady,
    updateProfile: notReady,
    joinTeam: notReady,
    leaveTeam: notReady,
    promoteToAdmin: notReady,
    demoteFromAdmin: notReady,
    removeMemberFromTeam: notReady,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const authData = useAuth();

  return (
    <AuthContext.Provider value={authData}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    const err = new Error('useAuthContext must be used within an AuthProvider');
    console.error(err);

    // Self-heal: this error can happen due to stale module caches/HMR glitches.
    // Reload once per tab session to rehydrate the correct provider tree.
    try {
      const key = 'auth_context_missing_reloaded';
      if (typeof window !== 'undefined' && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        setTimeout(() => window.location.reload(), 0);
      }
    } catch {
      // ignore
    }

    // Prevent blank screen: return a safe fallback.
    return createMissingAuthContextFallback();
  }
  return context;
}
